import { ReactNode } from 'react';
import { create } from 'zustand';
import { SectionFields } from '@/types/general';

export interface Notification {
  id?: string;
  open?: boolean;
  title?: string;
  message?: string;
  content?: ReactNode;
  defaultValues?: Record<string, any>;
  defaultValuesProductionLocation?: Record<string, any>;
  defaultValuesMaterial?: Record<string, any>;
  severity?: 'error' | 'warning' | 'info' | 'success' | 'secondary' | 'primary';
  mode?: 'loading' | 'normal';
  type?: 'snackbar' | 'dialog' | 'form' | 'banner' | 'image';
  formFields?: SectionFields[];
  cancelText?: string;
  confirmText?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  onSubmit?: (_: Record<string, any>) => void;
  blur?: boolean;
  valueId?: string;
  html?: string;
}

interface NotificationState {
  notification: Notification;
  snackbarQueue: Notification[];
  showNotification: (props: Notification) => void;
  handleClose: () => void;
  removeSnackbar: (id: string) => void;
}

const createNotificationId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const DEFAULT_NOTIFICATION: Notification = {
  open: false,
  severity: 'info',
  mode: 'normal',
  type: 'banner',
};

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notification: DEFAULT_NOTIFICATION,
  snackbarQueue: [],

  showNotification: (props: Notification) => {
    const notification = { ...DEFAULT_NOTIFICATION, ...props, open: true };

    if (notification.type === 'snackbar') {
      const { message, html } = notification;
      if (typeof message === 'string' && !html && !message?.trim()) return;

      const { snackbarQueue } = get();
      const trimmedMessage = message.trim();

      // Prevent duplicate messages
      if (snackbarQueue.some((n) => n.message === trimmedMessage)) return;

      // Remove loading notifications if new one is not loading
      if (notification.mode !== 'loading') {
        set((state) => ({
          snackbarQueue: state.snackbarQueue.filter((n) => n.mode !== 'loading'),
        }));
      }

      const id = createNotificationId();
      const newSnackbar = { ...notification, id, message: trimmedMessage };

      set((state) => ({
        snackbarQueue: [...state.snackbarQueue, newSnackbar],
      }));

      // Auto-remove after 10 seconds
      setTimeout(() => get().removeSnackbar(id), 10000);
    } else {
      set({ notification });
    }
  },
  handleClose: () => {
    set({ notification: DEFAULT_NOTIFICATION });
  },

  removeSnackbar: (id: string) => {
    set((state) => ({
      snackbarQueue: state.snackbarQueue.filter((snackbar) => snackbar.id !== id),
    }));
  },
}));

// Utility hook for common notification patterns
export const useNotifications = () => {
  const showNotification = useNotificationStore((state) => state.showNotification);

  return {
    success: (message: string, title?: string) =>
      showNotification({ message, title, severity: 'success', type: 'snackbar' }),

    error: (message: string, title?: string, html?: string) =>
      showNotification({ message, title, severity: 'error', type: 'snackbar', html }),

    warning: (message: string, title?: string) =>
      showNotification({ message, title, severity: 'warning', type: 'snackbar' }),

    info: (message: string, title?: string) =>
      showNotification({ message, title, severity: 'info', type: 'snackbar' }),

    loading: (message: string, title?: string) =>
      showNotification({ message, title, severity: 'info', mode: 'loading', type: 'snackbar' }),

    banner: (message: string, title?: string, severity: Notification['severity'] = 'info') =>
      showNotification({ message, title, severity, type: 'banner' }),

    dialog: (props: Omit<Notification, 'type'>) => showNotification({ ...props, type: 'dialog' }),

    form: (props: Omit<Notification, 'type'>) => showNotification({ ...props, type: 'form' }),

    image: (props: Omit<Notification, 'type'>) => showNotification({ ...props, type: 'image' }),

    close: () => {
      return useNotificationStore.getState().handleClose();
    },
  };
};
